import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";

interface CountdownTimerProps {
  endDate: string;
  endTime?: string;
  startDate?: string;
  startTime?: string;
  compact?: boolean;
  isActive?: boolean; // Add isActive prop to respect backend status
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function CountdownTimer({ 
  endDate, 
  endTime, 
  startDate, 
  startTime, 
  compact = false,
  isActive = true 
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  
  const [isExpired, setIsExpired] = useState(false);
  const [isCountingToStart, setIsCountingToStart] = useState(false);

  // Fetch current server time in SA timezone
  const { data: serverTimeData } = useQuery({
    queryKey: ['/api/server-time'],
    refetchInterval: 30000, // Refresh every 30 seconds to stay synchronized
    staleTime: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Use server time if available, otherwise fallback to local time
      let currentTime: Date;
      if (serverTimeData?.serverTime) {
        // Add time elapsed since server fetch to current time
        const serverFetchTime = new Date(serverTimeData.serverTime);
        const now = new Date();
        const timeSinceServerFetch = now.getTime() - serverFetchTime.getTime();
        currentTime = new Date(serverFetchTime.getTime() + timeSinceServerFetch);
      } else {
        // Fallback: create a local time approximation adjusted for SA timezone
        const localTime = new Date();
        currentTime = new Date(localTime.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
      }
      
      // Calculate start datetime if provided
      let targetStartDate: Date | null = null;
      if (startDate) {
        const startDateOnly = new Date(startDate).toISOString().split('T')[0];
        const startTimeStr = startTime || '00:00';
        targetStartDate = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`);
      }
      
      // Calculate end datetime with consistent SA timezone logic
      let targetEndDate: Date;
      if (endTime) {
        // Parse the endDate as UTC and extract just the date part
        const endDateUTC = new Date(endDate);
        const year = endDateUTC.getUTCFullYear();
        const month = endDateUTC.getUTCMonth();
        const day = endDateUTC.getUTCDate();
        
        // Create SA timezone date with the competition end time
        const [hours, minutes] = endTime.split(':').map(Number);
        targetEndDate = new Date();
        targetEndDate.setFullYear(year, month, day);
        targetEndDate.setHours(hours, minutes, 59, 999);
        
        // Convert to SA timezone
        const saTimeString = targetEndDate.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' });
        targetEndDate = new Date(saTimeString);
      } else {
        // If no endTime specified, use end of day in SA timezone
        const endDateUTC = new Date(endDate);
        const year = endDateUTC.getUTCFullYear();
        const month = endDateUTC.getUTCMonth();
        const day = endDateUTC.getUTCDate();
        
        targetEndDate = new Date();
        targetEndDate.setFullYear(year, month, day);
        targetEndDate.setHours(23, 59, 59, 999);
        
        const saTimeString = targetEndDate.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' });
        targetEndDate = new Date(saTimeString);
      }
      
      // Determine what to count down to
      let targetDate: Date;
      let countingToStart = false;
      
      if (targetStartDate && targetStartDate > currentTime) {
        // Competition hasn't started yet - count to start
        targetDate = targetStartDate;
        countingToStart = true;
      } else {
        // Competition is active or start time passed - count to end
        targetDate = targetEndDate;
        countingToStart = false;
      }
      
      setIsCountingToStart(countingToStart);
      
      const difference = targetDate.getTime() - currentTime.getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate, endTime, startDate, startTime, serverTimeData?.serverTime]);

  if (!isActive) {
    return (
      <div className="text-center py-4">
        <div className="text-lg font-semibold text-destructive">
          Competition Ended
        </div>
      </div>
    );
  }

  const TimeUnit = ({ value, label, color }: { value: number; label: string; color: string }) => (
    <div className="text-center">
      <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs min-w-[35px] font-bold mb-1">
        {value.toString().padStart(2, '0')}
      </div>
      <div className="text-[10px] text-white uppercase tracking-wide">
        {label}
      </div>
    </div>
  );

  if (compact) {
    return (
      <div className="flex gap-1">
        {timeLeft.days > 0 && (
          <TimeUnit value={timeLeft.days} label="DAYS" color="bg-gray-800" />
        )}
        <TimeUnit value={timeLeft.hours} label="HRS" color="bg-gray-800" />
        <TimeUnit value={timeLeft.minutes} label="MIN" color="bg-gray-800" />
        <TimeUnit value={timeLeft.seconds} label="SEC" color="bg-gray-800" />
      </div>
    );
  }

  return (
    <Card className="bg-primary text-white">
      <CardContent className="p-6">
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold">
            {isCountingToStart ? "Starts In" : "Time Remaining"}
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {timeLeft.days > 0 && (
            <div className="text-center">
              <div className="text-4xl font-bold mb-1">{timeLeft.days}</div>
              <div className="text-sm">Days</div>
            </div>
          )}
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{timeLeft.hours}</div>
            <div className="text-sm">Hours</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{timeLeft.minutes}</div>
            <div className="text-sm">Minutes</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-1">{timeLeft.seconds}</div>
            <div className="text-sm">Seconds</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
